/**
 * Upload transports (TO-392, Epic 25 F3): an integration (`api`, personal API
 * key) or a console user (`web`, session) starts an upload, sends the bytes to
 * a presigned `PUT`, and completes it into F2's receipt.
 */
import type { LogicalFileType, RetrievalMethodType } from './file-exchange.interface.js'
import type { ImportExecutionStatus, MalwareScanVerdict } from './import-execution.js'

/** The two upload transports — the `api` / `web` members of {@link RetrievalMethodType}. */
export type UploadTransport = RetrievalMethodType.API | RetrievalMethodType.WEB

/** Which credential admitted the request (TO-392 BR-3): `api` needs a key, `web` a session. */
export enum UploadCredential {
	API_KEY = 'API_KEY',
	SESSION = 'SESSION',
}

/** `details.reason` of `IMPORT_TRANSPORT_NOT_ALLOWED`. */
export enum ImportTransportRefusal {
	CREDENTIAL_MISMATCH = 'CREDENTIAL_MISMATCH',
	NOT_CONFIGURED = 'NOT_CONFIGURED',
}

/** Upload refusal codes outside the shared HTTP enums: the 429 has no shared helper (TO-392 BR-6). */
export enum ImportUploadErrorCode {
	IMPORT_DAILY_LIMIT_REACHED = 'IMPORT_DAILY_LIMIT_REACHED',
}

/** What a complete did: a new receipt, or content another import of the file already holds. */
export enum ImportUploadOutcome {
	RECEIVED = 'received',
	DUPLICATE = 'duplicate',
}

/** `POST /file-exchange/imports` body. */
export interface ImportUploadStartRequest {
	logicalFile: LogicalFileType
	transport: UploadTransport
	filename: string
	sizeBytes: number
	/** Lowercase hex SHA-256 of the bytes that will be sent. */
	sha256: string
}

/** The public view of one import execution — never its tenant, bucket or object key. */
export interface ImportStatusDTO {
	id: string
	logicalFile: LogicalFileType
	transport: RetrievalMethodType
	originalFilename: string
	status: ImportExecutionStatus
	sizeBytes: number | null
	sha256: string | null
	scanVerdict: MalwareScanVerdict | null
	failureCode: string | null
	createdAt: string
	updatedAt: string
	receivedAt: string | null
	readyAt: string | null
}

/** How to send the bytes: exactly `sizeBytes` bytes, with `headers`, before `expiresAt`. */
export interface ImportUploadInstructions {
	method: 'PUT'
	url: string
	headers: Record<string, string>
	expiresAt: string
}

/** `201` body of a start. */
export interface ImportUploadStartResponse {
	import: ImportStatusDTO
	upload: ImportUploadInstructions
}

/** `200` body of a complete; on `duplicate`, `import` is the execution that holds the content. */
export interface ImportUploadCompleteResponse {
	outcome: ImportUploadOutcome
	import: ImportStatusDTO
}
